/**
 * The warning before a repetition loses the game.
 *
 * A player lost a rated game (DP3EUT6KK2, 2026-10-25) by perpetual check and
 * wrote in: "I only moved my horse around a few times and you tell me I
 * repeated check?" The ruling was right -- the sole perpetual checker loses
 * (WXF) -- but nothing on the board had said so until the game was over. Good
 * xiangqi software says "长将，请变着" before the losing repetition, and this is
 * that sentence.
 *
 * Pure, like endreason.ts: a function of a move list and a side, so it can be
 * tested without a DOM. The ruling it predicts is the one Game.outcome() makes
 * and the server applies, computed by the same Game, so the warning cannot
 * disagree with the verdict.
 *
 * It fires on `side`'s turn, while the game is still going, in either of two
 * situations:
 *
 *  1. The position on the board has already occurred at least twice, and in the
 *     cycle that brought it back `side` reached a strictly higher violation than
 *     its opponent (it checked on every move while the opponent did not, or,
 *     with no check anywhere in the cycle, it chased the same piece on every
 *     move while the opponent did not). Equal fault is a draw and warns nobody.
 *
 *  2. Some move of `side`, or some move of `side` followed by some reply, would
 *     bring a position round for the third time with `side` ruled against. This
 *     is the case (1) misses: in DP3EUT6KK2 the checked king stepped aside once
 *     (ply 53), so the position on the board was new -- and then stepped back
 *     after Black's next check to complete the third occurrence. Without this
 *     the warning shown after ply 51 would have vanished on the very turn that
 *     decided the game.
 *
 * Cost: one replay of the game, then for each non-capturing move and reply a
 * board key (a capture can never recreate an earlier position, since material
 * only goes down). Only the rare move pair that lands on a position already
 * seen twice gets a full replay to ask the Game for its ruling.
 */

import {
  Color,
  Game,
  GameResult,
  legalMoves,
  parseFen,
  repetitionKey,
  START_FEN,
  violationLevels,
  ViolationLevel,
  type CyclePly,
  type Move,
} from "@xq/rules";

export type RepetitionWarning = "perpetual-check" | "perpetual-chase";

/** `side`'s violation if the cycle rules against it, else null. */
function faultOf(cycle: readonly CyclePly[] | null, side: Color): RepetitionWarning | null {
  if (!cycle || cycle.length === 0) return null;
  const levels = violationLevels(cycle);
  const mine = side === Color.Red ? levels.red : levels.black;
  const theirs = side === Color.Red ? levels.black : levels.red;
  if (mine <= theirs) return null;
  return mine === ViolationLevel.Check ? "perpetual-check" : "perpetual-chase";
}

function replay(moves: readonly Move[]): Game | null {
  const game = new Game(parseFen(START_FEN));
  try {
    for (const m of moves) game.play(m);
  } catch {
    return null; // a history this client cannot replay warns about nothing
  }
  return game;
}

/** If playing `extra` after `moves` ends the game by repetition against `side`, what for. */
function losesBy(moves: readonly Move[], extra: readonly Move[], side: Color): RepetitionWarning | null {
  const game = replay(moves);
  if (!game) return null;
  for (const m of extra) {
    // A game that ends on the first of the two moves never reaches the second.
    if (game.outcome() !== GameResult.Ongoing) return null;
    game.play(m);
  }
  const lost = side === Color.Red ? GameResult.BlackWins : GameResult.RedWins;
  if (game.status() !== GameResult.Ongoing) return null; // a mate is a mate, not a repetition
  if (game.repetitionOutcome() !== lost) return null;
  return faultOf(game.repetitionCycle(), side);
}

/** What `side`, about to move, is in danger of losing by, or null. */
export function repetitionWarning(moves: readonly Move[], side: Color): RepetitionWarning | null {
  const game = replay(moves);
  if (!game) return null;
  const pos = game.position;
  if (pos.sideToMove !== side) return null;
  if (game.outcome() !== GameResult.Ongoing) return null;

  // (1) The position on the board is one recurrence away from a ruling.
  if (game.currentRepetitionCount() >= 2) {
    const fault = faultOf(game.repetitionCycle(), side);
    if (fault) return fault;
  }

  // (2) A move, or a move and a reply, completes a third occurrence.
  const seen = new Map<string, number>();
  {
    const p = parseFen(START_FEN);
    seen.set(repetitionKey(p), 1);
    for (const m of moves) {
      p.makeMove(m);
      const k = repetitionKey(p);
      seen.set(k, (seen.get(k) ?? 0) + 1);
    }
  }
  const twice = (k: string) => (seen.get(k) ?? 0) >= 2;
  if (![...seen.values()].some((n) => n >= 2)) return null; // nothing has come round twice yet

  const work = pos.clone();
  for (const m of legalMoves(work)) {
    if (work.board[m.to]) continue; // captures cannot repeat
    const u1 = work.makeMoveFast(m);
    let found: RepetitionWarning | null = null;
    if (twice(repetitionKey(work))) found = losesBy(moves, [m], side);
    if (!found) {
      for (const r of legalMoves(work)) {
        if (work.board[r.to]) continue;
        const u2 = work.makeMoveFast(r);
        const hit = twice(repetitionKey(work));
        work.unmakeMove(u2);
        if (hit) {
          found = losesBy(moves, [m, r], side);
          if (found) break;
        }
      }
    }
    work.unmakeMove(u1);
    if (found) return found;
  }
  return null;
}
